import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  InputAdornment,
  Grid,
  Card,
  CardContent,
  Avatar,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  School as SchoolIcon,
  Person as PersonIcon,
  Work as WorkIcon,
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import axios from 'axios';
import { motion } from 'framer-motion';
import TeacherForm from '../../components/TeacherForm';

const TeacherManagement = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingTeacher, setEditingTeacher] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    male: 0,
    female: 0
  });

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    date_of_birth: '',
    gender: '',
    qualification: '',
    subject_specialization: '',
    joining_date: '',
    experience: '',
    employee_id: '',
    salary: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'Nepal',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    emergency_contact_relationship: '',
    notes: ''
  });

  useEffect(() => {
    fetchTeachers();
    fetchStats();
  }, [page, rowsPerPage, searchTerm, statusFilter]);

  const fetchTeachers = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/teachers', {
        params: {
          page: page + 1,
          limit: rowsPerPage,
          search: searchTerm,
          status: statusFilter
        }
      });
      
      if (response.data.success) {
        setTeachers(response.data.data.teachers || []);
        setTotalCount(response.data.data.total || 0);
      } else {
        // Handle case when backend returns success: false
        setTeachers([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Error fetching teachers:', error);
      // Only show error message for actual errors, not for empty data
      if (error.response?.status !== 200) {
        enqueueSnackbar('Failed to fetch teachers', { variant: 'error' });
      }
      setTeachers([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get('/api/teachers/stats/overview');
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching teacher stats:', error);
    }
  };

  const handleSubmit = async () => {
    try {
      const url = editingTeacher ? `/api/teachers/${editingTeacher.teacher_id}` : '/api/teachers';
      const method = editingTeacher ? 'put' : 'post';
      
      const response = await axios[method](url, formData);
      
      if (response.data.success) {
        enqueueSnackbar(
          `Teacher ${editingTeacher ? 'updated' : 'created'} successfully`,
          { variant: 'success' }
        );
        setOpenDialog(false);
        resetForm();
        fetchTeachers();
        fetchStats();
      }
    } catch (error) {
      console.error('Error saving teacher:', error);
      enqueueSnackbar(
        `Failed to ${editingTeacher ? 'update' : 'create'} teacher`,
        { variant: 'error' }
      );
    }
  };

  const handleEdit = (teacher) => {
    setEditingTeacher(teacher);
    setFormData({
      first_name: teacher.first_name || '',
      last_name: teacher.last_name || '',
      date_of_birth: teacher.date_of_birth ? teacher.date_of_birth.split('T')[0] : '',
      gender: teacher.gender || '',
      qualification: teacher.qualification || '',
      subject_specialization: teacher.subject_specialization || '',
      joining_date: teacher.joining_date ? teacher.joining_date.split('T')[0] : '',
      experience: teacher.experience || '',
      employee_id: teacher.employee_id || '',
      salary: teacher.salary || '',
      phone: teacher.phone || '',
      email: teacher.email || '',
      address: teacher.address || '',
      city: teacher.city || '',
      state: teacher.state || '',
      postal_code: teacher.postal_code || '',
      country: teacher.country || 'Nepal',
      emergency_contact_name: teacher.emergency_contact_name || '',
      emergency_contact_phone: teacher.emergency_contact_phone || '',
      emergency_contact_relationship: teacher.emergency_contact_relationship || '',
      notes: teacher.notes || ''
    });
    setOpenDialog(true);
  };

  const handleDelete = async (teacherId) => {
    if (window.confirm('Are you sure you want to delete this teacher?')) {
      try {
        const response = await axios.delete(`/api/teachers/${teacherId}`);
        if (response.data.success) {
          enqueueSnackbar('Teacher deleted successfully', { variant: 'success' });
          fetchTeachers();
          fetchStats();
        }
      } catch (error) {
        console.error('Error deleting teacher:', error);
        enqueueSnackbar('Failed to delete teacher', { variant: 'error' });
      }
    }
  };

  const handleToggleStatus = async (teacherId, currentStatus) => {
    try {
      const response = await axios.put(`/api/teachers/${teacherId}/toggle-status`);
      if (response.data.success) {
        enqueueSnackbar(
          `Teacher ${currentStatus === 'active' ? 'deactivated' : 'activated'} successfully`,
          { variant: 'success' }
        );
        fetchTeachers();
        fetchStats();
      }
    } catch (error) {
      console.error('Error toggling teacher status:', error);
      enqueueSnackbar('Failed to toggle teacher status', { variant: 'error' });
    }
  };

  const resetForm = () => {
    setFormData({
      first_name: '',
      last_name: '',
      date_of_birth: '',
      gender: '',
      qualification: '',
      subject_specialization: '',
      joining_date: '',
      experience: '',
      employee_id: '',
      salary: '',
      phone: '',
      email: '',
      address: '',
      city: '',
      state: '',
      postal_code: '',
      country: 'Nepal',
      emergency_contact_name: '',
      emergency_contact_phone: '',
      emergency_contact_relationship: '',
      notes: ''
    });
    setEditingTeacher(null);
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'error';
      case 'on_leave': return 'warning';
      default: return 'default';
    }
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold' }}>
        Teacher Management
      </Typography>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                      {stats.total}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                      Total Teachers
                    </Typography>
                  </Box>
                  <SchoolIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <Card sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                      {stats.active}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                      Active Teachers
                    </Typography>
                  </Box>
                  <PersonIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Card sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                      {stats.male}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                      Male Teachers
                    </Typography>
                  </Box>
                  <WorkIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <Card sx={{ background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', color: 'white' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                      {stats.female}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                      Female Teachers
                    </Typography>
                  </Box>
                  <PersonIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>
      </Grid>

      {/* Controls */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Search teachers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="">All Status</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
                <MenuItem value="on_leave">On Leave</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={5}>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={fetchTeachers}
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  resetForm();
                  setOpenDialog(true);
                }}
              >
                Add Teacher
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Teachers Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Teacher</TableCell>
                <TableCell>Teacher Code</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Qualification</TableCell>
                <TableCell>Subject</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">Loading...</TableCell>
                </TableRow>
              ) : teachers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Box sx={{ py: 4, textAlign: 'center' }}>
                      <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                        No Teachers Found
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {searchTerm || statusFilter 
                          ? 'No teachers match your search criteria' 
                          : 'No teachers have been added yet'}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                teachers.map((teacher) => (
                  <TableRow key={teacher.teacher_id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ bgcolor: 'primary.main' }}>
                          {getInitials(teacher.first_name, teacher.last_name)}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 'medium' }}>
                            {teacher.first_name} {teacher.last_name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {teacher.gender} • {teacher.experience}+ years exp
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {teacher.teacher_code}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">{teacher.phone}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {teacher.email}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{teacher.qualification}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={teacher.subject_specialization || 'General'}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={teacher.status?.charAt(0).toUpperCase() + teacher.status?.slice(1) || 'Active'}
                        color={getStatusColor(teacher.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <Tooltip title="Edit Teacher">
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(teacher)}
                            color="primary"
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={teacher.status === 'active' ? 'Deactivate' : 'Activate'}>
                          <IconButton
                            size="small"
                            onClick={() => handleToggleStatus(teacher.teacher_id, teacher.status)}
                            color={teacher.status === 'active' ? 'warning' : 'success'}
                          >
                            {teacher.status === 'active' ? <ToggleOffIcon /> : <ToggleOnIcon />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Teacher">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(teacher.teacher_id)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ pb: 1, fontWeight: 'bold' }}>
          {editingTeacher ? 'Edit Teacher' : 'Add New Teacher'}
        </DialogTitle>
        <TeacherForm formData={formData} setFormData={setFormData} />
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button onClick={() => setOpenDialog(false)} size="large">
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            size="large"
            sx={{ minWidth: 120 }}
          >
            {editingTeacher ? 'Update' : 'Create'} Teacher
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TeacherManagement;
